/** Cache of emoji → data URL masks to avoid re-rendering. */
const cache = new Map<string, string>();

/** Check if a value is a valid emoji for avatar shape. */
export function isEmoji(value: string): boolean {
  if (!value || value.length === 0) return false;
  if (value.length > 20) return false;
  return /[^\x00-\x7F]/.test(value);
}

/** Validate an avatar shape value from metadata. */
export function isValidAvatarShape(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0) return false;
  return isEmoji(value);
}

/** Extract avatar shape from metadata, returning undefined if invalid. */
export function getAvatarShape(metadata: Record<string, unknown> | undefined): string | undefined {
  const raw = metadata?.shape;
  return isValidAvatarShape(raw) ? raw : undefined;
}

/**
 * Generate a PNG data-URL mask from an emoji.
 * 
 * Algorithm:
 * 1. Draw the emoji at 512px on an oversized (768×768) scratch canvas.
 * 2. Scan pixels for the tight bounding box (alpha threshold = 25).
 * 3. Square the crop so nothing stretches.
 * 4. Redraw the squared crop onto a 256×256 output canvas.
 * 5. Convert to alpha mask (white RGB, keep original alpha).
 * 6. Export as PNG data-URL and cache.
 */
export function getEmojiMaskUrl(emoji: string): string {
  const cached = cache.get(emoji);
  if (cached) return cached;

  const fontSize = 512;
  const scratch = Math.ceil(fontSize * 1.5); // 768

  // Step 1: Draw emoji on scratch canvas
  const scratchCanvas = document.createElement('canvas');
  scratchCanvas.width = scratch;
  scratchCanvas.height = scratch;
  const sCtx = scratchCanvas.getContext('2d');
  if (!sCtx) return '';

  sCtx.textAlign = 'center';
  sCtx.textBaseline = 'middle';
  sCtx.font = `${fontSize}px serif`;
  sCtx.fillText(emoji, scratch / 2, scratch / 2);

  // Step 2: Find tight bounding box
  const imageData = sCtx.getImageData(0, 0, scratch, scratch);
  const { data, width, height } = imageData;
  const threshold = 25;

  let minX = width, minY = height, maxX = 0, maxY = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const alpha = data[(y * width + x) * 4 + 3];
      if (alpha > threshold) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  // If nothing found, return empty
  if (maxX <= minX || maxY <= minY) return '';

  // Step 3: Square the crop (expand shorter axis, centered)
  const cropW = maxX - minX + 1;
  const cropH = maxY - minY + 1;
  const side = Math.max(cropW, cropH);

  const offsetX = minX - Math.floor((side - cropW) / 2);
  const offsetY = minY - Math.floor((side - cropH) / 2);

  // Step 4: Redraw onto 256×256
  const outSize = 256;
  const outCanvas = document.createElement('canvas');
  outCanvas.width = outSize;
  outCanvas.height = outSize;
  const oCtx = outCanvas.getContext('2d');
  if (!oCtx) return '';

  oCtx.drawImage(scratchCanvas, offsetX, offsetY, side, side, 0, 0, outSize, outSize);

  // Step 5: Convert to alpha mask (white RGB + original alpha)
  const outData = oCtx.getImageData(0, 0, outSize, outSize);
  const od = outData.data;
  for (let i = 0; i < od.length; i += 4) {
    od[i] = 255;     // R → white
    od[i + 1] = 255; // G → white
    od[i + 2] = 255; // B → white
    // Alpha stays as-is
  }
  oCtx.putImageData(outData, 0, 0);

  // Step 6: Export and cache
  const url = outCanvas.toDataURL('image/png');
  cache.set(emoji, url);
  return url;
}

/** CSS style object for applying drop-shadow border to shaped avatars. */
export function getShapedAvatarBorderStyle(color = 'hsl(var(--background))'): Record<string, string> {
  const px = 3;
  return {
    filter:
      `drop-shadow(${px}px 0 0 ${color})` +
      ` drop-shadow(-${px}px 0 0 ${color})` +
      ` drop-shadow(0 ${px}px 0 ${color})` +
      ` drop-shadow(0 -${px}px 0 ${color})`,
  };
}
